// The leader's home: three views of the floor behind one rail.
//
// Overview is what needs them and how the day is going; Work centres is every
// station by department; Buffers is every pile between two stations. The rail
// is not a place — a reload comes back to Overview — because the question a
// leader is answering changes faster than a URL should.

import { useT } from '@/i18n/useT';
import { useFloorPairs } from '@/queries';
import { useStore } from '@/store';
import type { FloorView } from '@/store/slices/ui';
import { FloorBuffers } from './FloorBuffers';
import { FloorOverview } from './FloorOverview';
import { FloorStations } from './FloorStations';

export function LeaderHome({
  tablet,
  flowlineIds,
  scopeLabel,
  onDrillTo,
  onOpenAlerts,
  onOpenCrew,
  onPickFlowline,
}: {
  tablet: boolean;
  flowlineIds: number[];
  /** "All flowlines" or "FL 2" — for the line under the rail. */
  scopeLabel: string;
  onDrillTo: (workcentreId: number, name: string) => void;
  onOpenAlerts: () => void;
  onOpenCrew: () => void;
  onPickFlowline: (flowlineId: number) => void;
}) {
  const t = useT();
  const view = useStore((s) => s.floorView);
  const setView = useStore((s) => s.setFloorView);
  const { pairs } = useFloorPairs(flowlineIds);
  const stationCount = new Set(pairs.map((p) => p.workcentre.id)).size;

  const views: Array<[FloorView, string]> = [
    ['overview', t.overviewT],
    ['stations', t.workCentresT],
    ['buffers', t.buffersT],
  ];
  const sub =
    view === 'stations'
      ? `${scopeLabel} · ${stationCount} ${t.workCentresT.toLowerCase()} · ${t.groupedByDeptT}`
      : view === 'buffers'
        ? scopeLabel
        : `${scopeLabel} · ${t.needsYouTargetsOutputT}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h1 className="sr-only">{t.productionT}</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          role="tablist"
          style={{ display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: 3 }}
        >
          {views.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              className="press"
              aria-selected={view === key}
              onClick={() => setView(key)}
              style={{
                borderRadius: 999,
                padding: '7px 14px',
                minHeight: 34,
                font: '700 11.5px Manrope,sans-serif',
                background: view === key ? 'var(--ink)' : 'transparent',
                color: view === key ? 'var(--bg)' : 'var(--sub)',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          ))}
        </span>
        <span
          style={{
            font: '500 12px Manrope,sans-serif',
            color: 'var(--sub)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {sub}
        </span>
      </div>

      {view === 'overview' && (
        <FloorOverview
          tablet={tablet}
          flowlineIds={flowlineIds}
          onDrillTo={onDrillTo}
          onOpenAlerts={onOpenAlerts}
          onOpenCrew={onOpenCrew}
          onPickFlowline={onPickFlowline}
        />
      )}
      {view === 'stations' && <FloorStations flowlineIds={flowlineIds} onDrillTo={onDrillTo} />}
      {view === 'buffers' && <FloorBuffers flowlineIds={flowlineIds} />}
    </div>
  );
}
